// src/utils/fileProcessing.ts
import ExcelJS from 'exceljs';
import { csvStringToJson } from '@/components/Analysis/Idms/utils';

/**
 * Parses a file as either CSV or XLSX based on its extension
 * @param file - The file to parse
 * @returns Promise resolving to the parsed data
 */
export const parseUploadedFile = (file: File): Promise<{ header: string[]; rows: string[][] }> => {
  return new Promise((resolve, reject) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (fileExtension === 'xlsx') {
      // Handle XLSX file
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = e.target?.result as ArrayBuffer;
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(data);

          // Get the first worksheet
          const worksheet = workbook.worksheets[0];

          if (!worksheet || worksheet.rowCount <= 1) {
            reject(new Error('The Excel file is empty'));
            return;
          }

          // Extract header row
          const headerRow = worksheet.getRow(1);
          const header: string[] = [];
          headerRow.eachCell((cell) => {
            header.push(cell.value?.toString() || '');
          });

          // Extract data rows
          const rows: string[][] = [];
          for (let i = 2; i <= worksheet.rowCount; i++) {
            const row = worksheet.getRow(i);
            const rowData: string[] = [];

            // Ensure we get values for all header columns
            for (let j = 1; j <= header.length; j++) {
              const cell = row.getCell(j);
              rowData.push(cell.value?.toString() || '');
            }

            rows.push(rowData);
          }

          resolve({ header, rows });
        } catch (error) {
          reject(new Error('Failed to parse Excel file'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read Excel file'));
      };

      reader.readAsArrayBuffer(file);
    } else if (fileExtension === 'csv') {
      // Handle CSV file
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const csvString = e.target?.result as string;
          const { header, rows } = csvStringToJson(csvString);

          // Convert Record<string, unknown>[] to string[][]
          const stringRows = rows.map((row) => header.map((key) => String(row[key] || '')));

          resolve({ header, rows: stringRows });
        } catch (error) {
          reject(new Error('Failed to parse CSV file'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read CSV file'));
      };

      reader.readAsText(file);
    } else {
      reject(new Error('Unsupported file format. Please upload an XLSX or CSV file.'));
    }
  });
};

export const xlsxToJson = async (
  file: File
): Promise<{
  header: string[];
  rows: Array<Record<string, unknown>>;
}> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = e.target?.result as ArrayBuffer;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(data);

        // Get the first worksheet
        const worksheet = workbook.worksheets[0];

        if (!worksheet || worksheet.rowCount <= 1) {
          reject(new Error('The Excel file is empty'));
          return;
        }

        // Extract header row
        const headerRow = worksheet.getRow(1);
        const header: string[] = [];
        headerRow.eachCell((cell) => {
          header.push(cell.value?.toString() || '');
        });

        // Extract data rows as objects
        const rows: Array<Record<string, unknown>> = [];
        for (let i = 2; i <= worksheet.rowCount; i++) {
          const row = worksheet.getRow(i);
          const rowData: Record<string, unknown> = {};

          // Map each cell to its header
          header.forEach((headerText, index) => {
            const cell = row.getCell(index + 1);
            rowData[headerText] = cell.value?.toString() || '';
          });

          rows.push(rowData);
        }

        resolve({ header, rows });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsArrayBuffer(file);
  });
};

// Function to handle both CSV and XLSX files
export const fileToJson = async (
  file: File
): Promise<{
  header: string[];
  rows: Array<Record<string, unknown>>;
}> => {
  try {
    // Use the existing parseUploadedFile function
    const { header, rows } = await parseUploadedFile(file);

    // Convert string[][] to Array<Record<string, unknown>>
    const recordRows = rows.map((row) => {
      return header.reduce((accumulator, key, index) => {
        accumulator[key] = row[index] === undefined ? '' : row[index];
        return accumulator;
      }, {} as Record<string, unknown>);
    });

    return { header, rows: recordRows };
  } catch (error) {
    console.error('Error parsing file:', error);
    throw error;
  }
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};
